/**
 * Migration: dep-centric playEngineConfig → global asset-map format.
 *
 * Old (dep-centric) shape — asset bytes/refs live on each dependency:
 *   components[].dependencies[] = { key, type, source, sourceRef }
 *   localization.overrides[lang][compId].dependencyOverrides[depKey] =
 *       { source, sourceRef, type }   // enriched object
 *
 * New (asset-map) shape — a shared global registry keyed by short asset key:
 *   assets[assetKey] = { type, bucket, name?, sourceRef, source }
 *   components[].dependencies[] = { key, type, assetKey }
 *   localization.overrides[lang][compId].dependencyOverrides[depKey] = assetKey
 *
 * Identical (type + sourceRef) assets are de-duplicated into one shared entry,
 * so components that referenced the same media collapse onto one asset handle.
 */

import _ from "lodash";
import { genAssetKey } from "./playEngineAssetUtils";

/**
 * Returns true when the config still uses the dep-centric format and should be
 * migrated to the asset-map format. Configs that already have a keyed `assets`
 * map, the very old `assets` array (handled by the engine's legacy path), or
 * are empty/fresh are left untouched.
 */
export function needsAssetMapMigration(config) {
  if (!config || typeof config !== "object") return false;

  const assets = config.assets;
  const hasKeyedMap = assets && !Array.isArray(assets) && Object.keys(assets).length > 0;
  if (hasKeyedMap) return false;
  // Leave the rare legacy `assets` array form to the engine's legacy restore.
  if (Array.isArray(assets) && assets.length > 0) return false;

  for (const comp of config.components || []) {
    for (const dep of comp.dependencies || []) {
      if (dep && !dep.assetKey && (dep.sourceRef || dep.source)) return true;
    }
  }

  const overrides = _.get(config, "localization.overrides") || {};
  for (const lang of Object.values(overrides)) {
    for (const comp of Object.values(lang || {})) {
      for (const v of Object.values(comp?.dependencyOverrides || {})) {
        if (v && typeof v === "object" && (v.sourceRef || v.source)) return true;
      }
    }
  }

  return false;
}

/** Stable de-dup key for an asset (type + its server reference). */
function dedupKey(type, sourceRef) {
  const refStr = typeof sourceRef === "string" ? sourceRef : JSON.stringify(sourceRef);
  return `${type}::${refStr}`;
}

/**
 * Convert a dep-centric playEngineConfig into the asset-map format.
 * Pure: returns a new config; does not mutate the input. Idempotent — running
 * it on an already-migrated config returns a clone unchanged.
 */
export function migrateConfigToAssetMap(config) {
  if (!needsAssetMapMigration(config)) return _.cloneDeep(config || {});

  const out = _.cloneDeep(config);
  const assets = {};
  const refIndex = new Map(); // dedupKey -> assetKey

  function ensureAsset(type, sourceRef, bucket, name) {
    if (!sourceRef) return null;
    const dk = dedupKey(type, sourceRef);
    if (refIndex.has(dk)) return refIndex.get(dk);
    const key = genAssetKey(type, Object.keys(assets));
    assets[key] = {
      type,
      bucket: bucket || "nobucket",
      ...(name ? { name } : {}),
      sourceRef,
      source: sourceRef, // hydrated to base64 at load/build time
    };
    refIndex.set(dk, key);
    return key;
  }

  // Component dependencies
  for (const comp of out.components || []) {
    const bucket = comp.bucket || "nobucket";
    for (const dep of comp.dependencies || []) {
      if (!dep) continue;
      if (dep.assetKey) continue; // already migrated
      const ref = dep.sourceRef ?? dep.source ?? null;
      if (ref) {
        const assetKey = ensureAsset(dep.type, ref, bucket, null);
        if (assetKey) dep.assetKey = assetKey;
      }
      delete dep.source;
      delete dep.sourceRef;
      delete dep.assetId;
      delete dep.isSatisfied;
    }
  }

  // Localization dependency overrides (enriched objects → asset key strings)
  const overrides = _.get(out, "localization.overrides");
  if (overrides && typeof overrides === "object") {
    for (const langOverrides of Object.values(overrides)) {
      if (!langOverrides) continue;
      for (const [compId, compOverride] of Object.entries(langOverrides)) {
        const depOverrides = compOverride?.dependencyOverrides;
        if (!depOverrides) continue;
        const ownerBucket =
          (out.components || []).find((c) => c.id === compId)?.bucket || "nobucket";
        for (const [depKey, entry] of Object.entries(depOverrides)) {
          if (typeof entry === "string") continue; // already an asset key
          if (!entry || typeof entry !== "object") continue;
          const ref = entry.sourceRef ?? entry.source ?? null;
          const assetKey = ensureAsset(entry.type, ref, ownerBucket, null);
          depOverrides[depKey] = assetKey || depOverrides[depKey];
        }
      }
    }
  }

  out.assets = assets;
  return out;
}
